import { Component, Inject, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { ComponentStore, tapResponse } from "@ngrx/component-store";
import { map, switchMap } from "rxjs/operators";
import { BasisOfValueAPIService } from 'src/app/core/v2/api-services/basis-of-value.service'
import { ApproachToValueAPIService } from 'src/app/core/v2/api-services/approach-to-value.service'
import { MethodToValueAPIService } from "src/app/core/v2/api-services/method-to-value.service";
import { PremiseOfValueAPIService } from "src/app/core/v2/api-services/premise-of-value.service";
import { TenureAPIService } from "src/app/core/v2/api-services/tenure.service";
import { combineLatest } from "rxjs";
import { formControlValueChanges } from "src/app/core/v2/utils";
import { ConsiderationTypeService } from "src/app/core/v2/services";
import { BankProposalValuationTaskService } from "./bank-proposal-valuation-task.service";

interface State {
  premiseOfValues: {id: number, name: string}[],
  baseOfValues: {id: number, name: string}[],
  approachesToValue: {id: number, name: string}[],
  methodsToValue: {id: number, name: string, approaches_to_value: number[]}[],
  tenures: {id: number, name: string}[],
  considerationTypes: {id: number, name: string}[]
}

@Component({
  selector: 'app-bank-proposal-valuation-task-dialog',
  templateUrl: './bank-proposal-valuation-task-dialog.component.html',
  providers: [ComponentStore]
})
export class BankProposalValuationTaskDialogComponent {
  form = new FormGroup({
    base_of_value_id: new FormControl<number>(1, [Validators.required]),
    premise_of_value_id: new FormControl<number>(null, [Validators.required]),
    approach_to_value_id: new FormControl<number>(null, [Validators.required]),
    method_to_value_id: new FormControl<number>(null, [Validators.required]),
    tenure_id: new FormControl<number>(null, [Validators.required]),
    consideration_type_id: new FormControl<number>(null, [Validators.required])
  })

  baseOfValues$ = this.componentStore.select(state => state.baseOfValues)
  premiseOfValues$ = this.componentStore.select(state => state.premiseOfValues)
  approachesToValue$ = this.componentStore.select(state => state.approachesToValue)
  methodsToValue$ = combineLatest([
    formControlValueChanges(this.form.controls.approach_to_value_id),
    this.componentStore.select(state => state.methodsToValue)
  ]).pipe(
    map(([approachToValueId, methodsToValue]) => {
      return methodsToValue
        .filter(item => item.approaches_to_value.includes(approachToValueId))
        .filter(item => {
          if (item.approaches_to_value.includes(2)) {
            return [29, 34, 35, 36].includes(item.id)
          }
          return true
        })
    })
  )
  tenures$ = this.componentStore.select(state => state.tenures)
  considerationTypes$ = this.componentStore.select(state => state.considerationTypes)

  constructor(
    public dialogRef: MatDialogRef<BankProposalValuationTaskDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      item: {
        task: {
          id: number
        }
      },
      isNewTask: boolean
    },
    private componentStore: ComponentStore<State>,
    private baseOfValueService: BasisOfValueAPIService,
    private premiseOfValueService: PremiseOfValueAPIService,
    private approachesToValueService: ApproachToValueAPIService,
    private methodsToValueService: MethodToValueAPIService,
    private tenureService: TenureAPIService,
    private considerationTypeService: ConsiderationTypeService,
    private bankProposalValuationTaskService: BankProposalValuationTaskService
  ) {
    this.componentStore.setState({
      baseOfValues: [],
      premiseOfValues: [],
      approachesToValue: [],
      methodsToValue: [],
      tenures: [],
      considerationTypes: []
    })

    this._fetchBaseOfValues()
    this._fetchPremiseOfValues()
    this._fetchApproachesToValue()
    this._fetchMethodsToValue()
    this._fetchTenures()
    this._fetchConsiderationTypes()
  }

  onClose() {
    this.dialogRef.close()
  }

  onSubmit() {
    this._submit()
  }

  private _submit = this.componentStore.effect<void>(trigger$ => 
    trigger$.pipe(
      switchMap(() => {
        return this.bankProposalValuationTaskService.updateValuationTask(
          this.data.item.task.id,
          {
            is_new_task: this.data.isNewTask, 
            base_of_value_id: this.form.controls.base_of_value_id.value,
            premise_of_value_id: this.form.controls.premise_of_value_id.value,
            approach_to_value_id: this.form.controls.approach_to_value_id.value,
            method_to_value_id: this.form.controls.method_to_value_id.value,
            tenure_id: this.form.controls.tenure_id.value,
            consideration_type_id: this.form.controls.consideration_type_id.value
          }
        ).pipe(
          tapResponse(
            (response) => {
              this.dialogRef.close(response)
            },
            (err) => {
              console.error(err)
            }
          )
        )
      })
    )
  )

  private _fetchBaseOfValues = this.componentStore.effect<void>(trigger$ => 
    trigger$.pipe(
      switchMap(() => 
        this.baseOfValueService.fetchListing().pipe(
          tapResponse(
            (response) => {
              this.componentStore.patchState({
                baseOfValues: response
              })
            },
            (err) => {
              console.error(err)
            }
          )
        )
      )
    )
  )

  private _fetchPremiseOfValues = this.componentStore.effect<void>(trigger$ => 
    trigger$.pipe(
      switchMap(() => 
        this.premiseOfValueService.fetchListing().pipe(
          tapResponse(
            (response) => {
              this.componentStore.patchState({
                premiseOfValues: response
              })
            },
            (err) => {
              console.error(err)
            }
          )
        )
      )
    )
  )

  private _fetchApproachesToValue = this.componentStore.effect<void>(trigger$ => 
    trigger$.pipe(
      switchMap(() => 
        this.approachesToValueService.fetchListing().pipe(
          tapResponse(
            (response) => {
              this.componentStore.patchState({
                approachesToValue: response
              })
            },
            (err) => {
              console.error(err)
            }
          )
        )
      )
    )
  )

  private _fetchMethodsToValue = this.componentStore.effect<void>(trigger$ => 
    trigger$.pipe(
      switchMap(() => 
        this.methodsToValueService.fetchListing().pipe(
          tapResponse(
            (response) => {
              this.componentStore.patchState({
                methodsToValue: response
              })
            },
            (err) => {
              console.error(err)
            }
          )
        )
      )
    )
  )

  private _fetchTenures = this.componentStore.effect<void>(trigger$ => 
    trigger$.pipe(
      switchMap(() => 
        this.tenureService.fetchListing().pipe(
          tapResponse(
            (response) => {
              this.componentStore.patchState({
                tenures: response
              })
            },
            (err) => {
              console.error(err)
            }
          )
        )
      )
    )
  )

  private _fetchConsiderationTypes = this.componentStore.effect<void>(trigger$ => 
    trigger$.pipe(
      switchMap(() => 
        this.considerationTypeService.fetchListing().pipe(
          tapResponse(
            (response) => {
              this.componentStore.patchState({
                considerationTypes: response
              })
            },
            (err) => {
              console.error(err)
            }
          )
        )
      )
    )
  )
}